import { useCallback, useEffect, useState } from 'react'

import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

import {
    apiCreateFeatherOrder,
    apiGetFeatherOrderStatus,
    apiGetFeatherOrdersByWallet,
    apiSignFeatherQuote,
    apiUpdateFeatherOrderWithDepositDetails,
} from '@src/api/feather.api'
import { ChainId } from '@src/configs/chainInfo'
import { StepNames } from '@src/enums'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { featherClient } from '@src/libs/featherClient'
import { FeatherOrderStatus } from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherOrderStatus'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import {
    IFeatherFormattedQuoteResponse,
    IFeatherOrderCreateResponse,
    IFeatherOrderStatusResponse,
} from '@src/types/feather'
import { Address } from '@src/types/token'
import { mapBackendToFrontendStatus } from '@src/utils/featherUtils'
import { createContextLogger } from '@src/utils/logger'
import { useApplicationStore, useHistoryStore } from '@src/zustand_stores'

const logger = createContextLogger('useFeatherOrder')

// TEMPORARY: lmao - Default Bitcoin address for testing, to be removed in production
export const DEFAULT_BITCOIN_ADDRESS = 'bc1paqglk025uslp2vnzyn88xqhf9740msym2zkr8fafjw4yj75az3hsll0449'

interface UseFeatherOrderProps {
    onOrderCreated: (orderData: IFeatherOrderCreateResponse) => void
    onOrderStatusChanged: (orderStatus: IFeatherOrderStatusResponse) => void
}

export const useFeatherOrder = ({ onOrderCreated, onOrderStatusChanged }: UseFeatherOrderProps) => {
    const [isCreatingOrder, setIsCreatingOrder] = useState(false)
    const [featherQuote, setFeatherQuote] = useState<IFeatherFormattedQuoteResponse | null>(null)
    const [orderId, setOrderId] = useState<string | null>(null)
    const [orderStatus, setOrderStatus] = useState<FeatherOrderStatus | null>(null)
    const [featherQuoteError, setFeatherQuoteError] = useState<string | null>(null)

    const { sellData, buyData, destinationAddress, quoteResponse, updateBuyData } = useExchangeDataContext()
    const { setExchangeId, setCurrentModalId } = useApplicationStore()
    const { updateHistory } = useHistoryStore()
    const { evmWalletAddress, solanaWalletAddress, tronWalletAddress } = useWalletConnections()

    // Create a Feather order
    const createOrder = useCallback(async () => {
        // Check that we have a feather order with a quote ID
        if (!sellData.isFeatherOrder || !sellData.featherQuoteId) {
            console.log('cannot create order: no feather quote ID available')
            return null
        }

        if (!destinationAddress) {
            console.log('cannot create order: no destination address')
            return null
        }

        // Get the appropriate wallet address based on source chain
        let sourceWalletAddress = (evmWalletAddress as string) || ''

        // If sellData is from Solana or Tron, use those wallet addresses
        if (sellData.chainId === 900) {
            // ChainId.Solana
            sourceWalletAddress = (solanaWalletAddress as string) || ''
        } else if (sellData.chainId === 728126428) {
            // ChainId.Tron
            sourceWalletAddress = (tronWalletAddress as string) || ''
        }

        if (!sourceWalletAddress) {
            console.log('no connected wallet found for source chain')
            return null
        }

        try {
            setIsCreatingOrder(true)

            const params = {
                quoteId: sellData.featherQuoteId,
                sourceWalletAddress: sourceWalletAddress,
            }

            console.log('creating Feather order with:', JSON.stringify(params, null, 2))

            try {
                // Create a new exchange ID for tracking
                const newExchangeId = uuidv4()
                setExchangeId(newExchangeId)
                setCurrentModalId(newExchangeId)

                // Call API to create order using the new FeatherClient
                const orderData = await apiCreateFeatherOrder(sellData.featherQuoteId, sourceWalletAddress)

                console.log('received Feather order response:', JSON.stringify(orderData, null, 2))

                // Update order state
                setOrderId(orderData.orderId)
                setOrderStatus(FeatherOrderStatus.WAITING_FOR_DEPOSIT)

                // Update history for tracking
                updateHistory(newExchangeId, {
                    sellData,
                    buyData,
                    orderId: orderData.orderId,
                    currentStep: StepNames.FEATHER_WAITING_FOR_DEPOSIT,
                })

                // Notify caller
                onOrderCreated(orderData)

                return orderData
            } catch (error) {
                logger.error('Error creating Feather order:', error)
                return null
            }
        } catch (error) {
            logger.error('Unexpected error in createOrder:', error)
            return null
        } finally {
            setIsCreatingOrder(false)
        }
    }, [
        sellData,
        destinationAddress,
        sellData.isFeatherOrder,
        sellData.featherQuoteId,
        setExchangeId,
        setCurrentModalId,
        updateHistory,
        onOrderCreated,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
    ])

    // Get a quote for a Feather order using the new FeatherClient
    const getQuote = useCallback(async () => {
        if (!sellData || !buyData || !quoteResponse) {
            return null
        }

        // Reset any previous errors when attempting a new quote fetch
        setFeatherQuoteError(null)

        // TEMPORARY: Use default Bitcoin address if none is provided and target is Bitcoin
        let targetAddress = destinationAddress
        if ((!targetAddress || targetAddress === '') && buyData.chainId === ChainId.Bitcoin) {
            targetAddress = DEFAULT_BITCOIN_ADDRESS as Address
        }

        // Don't fetch a quote if the sell amount is zero
        if (!sellData.value || sellData.value === BigInt(0)) {
            return null
        }

        try {
            // Get the appropriate wallet address based on source chain
            let sourceWalletAddress = (evmWalletAddress as string) || ''

            // If sellData is from Solana or Tron, use those wallet addresses
            if (sellData.chainId === ChainId.Solana) {
                sourceWalletAddress = (solanaWalletAddress as string) || ''
            } else if (sellData.chainId === ChainId.Tron) {
                sourceWalletAddress = (tronWalletAddress as string) || ''
            }

            if (!sourceWalletAddress) {
                logger.warn('No connected wallet found for source chain', { chainId: sellData.chainId })
                setFeatherQuoteError('No wallet connected for the source chain')
                return { error: 'No wallet connected for the source chain' }
            }

            // Get the destination token symbol and decimals
            const dstTokenSymbol = buyData.token?.symbol || 'BTC'
            const dstTokenDecimals = buyData.token?.decimals || 8 // Default to 8 for Bitcoin

            const quote = await featherClient.getQuote(
                sellData.chainId,
                sellData.token.address,
                sellData.value.toString(),
                buyData.chainId,
                targetAddress as Address,
                sellData.token.symbol,
                sellData.token.decimals,
                dstTokenSymbol,
                dstTokenDecimals,
                sourceWalletAddress
            )

            // Update the buyData.value with the buyAmount from the Feather quote
            if (quote.buyAmount) {
                try {
                    const buyAmountBigInt = BigInt(quote.buyAmount)

                    // Update buy data with the amount
                    updateBuyData({ value: buyAmountBigInt })

                    // Update feather quote state
                    setFeatherQuote(quote)
                    return quote
                } catch (error) {
                    const errorMessage = 'Failed to parse quote amount'
                    logger.error(errorMessage, error)
                    setFeatherQuoteError(errorMessage)
                    return { error: errorMessage }
                }
            } else {
                const errorMessage = 'Received empty quote amount from Feather'
                logger.error(errorMessage)
                setFeatherQuoteError(errorMessage)
                return { error: errorMessage }
            }
        } catch (error: any) {
            // Extract the error message from the API response if available
            let errorMessage = 'Failed to fetch Feather quote'
            let errorType = 'unknown'

            // Check for detail field in error response (this contains the full error message)
            if (error.response?.data?.detail) {
                const detailMessage = error.response.data.detail
                errorMessage = detailMessage

                // Check for trade amount too small in the detail message
                if (detailMessage.includes('Trade amount too small')) {
                    errorType = 'amount_too_small'
                    // Simplify the error message for display
                    errorMessage = 'Trade amount too small. Please increase your trade amount.'
                } else if (detailMessage.includes('Quote calculation error')) {
                    errorType = 'calculation_error'
                }
            }
            // Fallback to message field if no detail field
            else if (error.response?.data?.message) {
                errorMessage = `${error.response.data.message}`

                // Extract specific error types that we want to handle specially
                if (errorMessage.includes('Trade amount too small')) {
                    errorType = 'amount_too_small'
                } else if (errorMessage.includes('Quote calculation error')) {
                    errorType = 'calculation_error'
                }
            } else if (error.message) {
                errorMessage = `${error.message}`
            }

            logger.error('Feather quote error:', {
                error,
                message: errorMessage,
                errorType,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
            })

            setFeatherQuoteError(errorMessage)
            return {
                error: errorMessage,
                errorType,
            }
        }
    }, [
        sellData,
        buyData,
        quoteResponse,
        destinationAddress,
        updateBuyData,
        evmWalletAddress,
        solanaWalletAddress,
        tronWalletAddress,
    ])

    // Check order status using the new FeatherClient
    const checkOrderStatus = useCallback(
        async (orderIdToCheck: string) => {
            try {
                const status = await apiGetFeatherOrderStatus(orderIdToCheck)
                // Convert backend status to frontend status
                setOrderStatus(mapBackendToFrontendStatus(status.status))
                onOrderStatusChanged(status)
                return status
            } catch (error: any) {
                logger.error('Error checking order status:', error)
                return null
            }
        },
        [onOrderStatusChanged]
    )

    // Update order with deposit transaction using the new FeatherClient
    const updateOrderWithDeposit = useCallback(
        async (depositTxHash: string) => {
            if (!orderId) {
                toast.error('No active order to update')
                return null
            }

            try {
                const updatedStatus = await apiUpdateFeatherOrderWithDepositDetails(orderId, depositTxHash)
                // Convert backend status to frontend status
                setOrderStatus(mapBackendToFrontendStatus(updatedStatus.status))
                onOrderStatusChanged(updatedStatus)
                return updatedStatus
            } catch (error: any) {
                logger.error('Error updating order with deposit:', error)
                toast.error('Failed to update order with deposit transaction')
                return null
            }
        },
        [orderId, onOrderStatusChanged]
    )

    // Get order history for a wallet using the new FeatherClient
    const getOrderHistory = useCallback(async (walletAddress: string) => {
        try {
            return await apiGetFeatherOrdersByWallet(walletAddress)
        } catch (error: any) {
            logger.error('Error getting order history:', error)
            return []
        }
    }, [])

    /**
     * Sign a quote with the user's wallet signature
     * @param quoteId The ID of the quote to sign
     * @param signature The signature from the user's wallet
     * @param srcUserWallet The user's source wallet address
     */
    const signQuote = useCallback(async (quoteId: string, signature: string, srcUserWallet: string) => {
        try {
            return await apiSignFeatherQuote(quoteId, signature, srcUserWallet)
        } catch (error: any) {
            logger.error('Error signing Feather quote:', error)
            throw error
        }
    }, [])

    // Poll for status updates when there's an active order
    useEffect(() => {
        if (!orderId) return

        // Initial check
        void checkOrderStatus(orderId)

        // Set up polling interval
        const interval = setInterval(() => {
            void checkOrderStatus(orderId)
        }, 10000) // Check every 10 seconds

        return () => clearInterval(interval)
    }, [orderId, checkOrderStatus])

    return {
        isCreatingOrder,
        featherQuote,
        orderId,
        orderStatus,
        createOrder,
        getQuote,
        checkOrderStatus,
        updateOrderWithDeposit,
        getOrderHistory,
        signQuote,
        featherQuoteError,
    }
}
